import { NetworkStatus, gql, useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';

import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { useLDFlag } from 'src/lib/launchDarkly';

import { GraphRef } from '../hooks/useGraphRef';

// TODO: Replace me with isNetworkRequestSettled from @apollo/client once
// upgraded to 3.8.0 or later.
function isNetworkRequestSettled(networkStatus?: NetworkStatus) {
  return networkStatus === NetworkStatus.ready || NetworkStatus.error;
}

const minFetchRate = 10_000;

export const useIsProposalStale = ({ graphRef }: { graphRef: GraphRef }) => {
  const [initialLatestRevisionId, setInitialLatestRevisionId] = useState<
    string | null | undefined
  >(null);
  const [polledSinceLastChange, setPolledSinceLastChange] = useState(true);
  const fetchRate = Math.max(
    useLDFlag('gov-proposal-staleness-fetch-rate-ms'),
    minFetchRate,
  );

  const proposalStalenessQuery = useQuery<
    GraphQLTypes.ProposalStaleness,
    GraphQLTypes.ProposalStalenessVariables
  >(
    gql`
      query ProposalStaleness($graphId: ID!, $graphVariant: String!) {
        service(id: $graphId) {
          id
          variant(name: $graphVariant) {
            id
            proposal {
              id
              latestRevision {
                id
              }
            }
          }
        }
      }
    `,
    {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      variables: { ...graphRef },
      pollInterval: fetchRate,
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      // Checked and updated to handle changes for 3.8 upgrade
      onCompleted: () => setPolledSinceLastChange(true),
    },
  );

  const polledLatestRevisionId =
    proposalStalenessQuery.data?.service?.variant?.proposal?.latestRevision?.id;

  const isStale =
    !!initialLatestRevisionId &&
    polledSinceLastChange &&
    isNetworkRequestSettled(proposalStalenessQuery.networkStatus) &&
    initialLatestRevisionId !== polledLatestRevisionId;

  useEffect(() => {
    if (!initialLatestRevisionId && polledLatestRevisionId)
      setInitialLatestRevisionId(polledLatestRevisionId);
  }, [initialLatestRevisionId, polledLatestRevisionId]);

  return useMemo(
    () => ({
      isStale,
      setInitialLatestRevisionId: (id: string) => {
        setInitialLatestRevisionId(id);
        setPolledSinceLastChange(false);
      },
    }),
    [isStale],
  );
};
